import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Loader2, ClipboardCheck, CheckCircle, XCircle, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

function AnswerButtons({ value, onChange, allowNa, disabled }) {
  return (
    <div className="flex items-center gap-2 shrink-0 flex-wrap">
      <Button
        type="button"
        size="sm"
        variant={value === 'yes' ? 'default' : 'outline'}
        className={cn(value === 'yes' && 'bg-green-600 hover:bg-green-700')}
        onClick={() => !disabled && onChange('yes')}
        disabled={disabled}
      >
        <CheckCircle className="h-3.5 w-3.5 mr-1" />
        YES
      </Button>
      <Button
        type="button"
        size="sm"
        variant={value === 'no' ? 'default' : 'outline'}
        className={cn(value === 'no' && 'bg-red-600 hover:bg-red-700')}
        onClick={() => !disabled && onChange('no')}
        disabled={disabled}
      >
        <XCircle className="h-3.5 w-3.5 mr-1" />
        NO
      </Button>
      {allowNa && (
        <Button
          type="button"
          size="sm"
          variant={value === 'na' ? 'default' : 'outline'}
          onClick={() => !disabled && onChange('na')}
          disabled={disabled}
        >
          <Minus className="h-3.5 w-3.5 mr-1" />
          N/A
        </Button>
      )}
    </div>
  );
}

function buildInitialFormData(template, employeeName, assessorName) {
  const data = {};
  if (!template) return data;

  const today = new Date().toISOString().split('T')[0];

  for (const field of template.header_fields || []) {
    if (field.prefill === 'employee_name') {
      data[field.id] = employeeName || '';
    } else if (field.id === 'assessor_name' || field.id === 'observer_name') {
      data[field.id] = assessorName || '';
    } else if (field.type === 'date' && field.id === 'assessment_date') {
      data[field.id] = today;
    } else {
      data[field.id] = '';
    }
  }

  for (const section of template.sections || []) {
    const stype = section.type;
    if (stype === 'yes_no_questions' || stype === 'yes_no_na_questions') {
      for (const q of section.questions || []) {
        data[q.id] = '';
        data[`${q.id}_comments`] = '';
      }
    } else if (stype === 'checklist_table') {
      for (const item of section.items || []) {
        for (const col of section.columns || []) {
          data[`checklist_${item.id}_${col.id}`] = col.id === 'date' ? '' : '';
        }
      }
    } else if (stype === 'textarea') {
      data[section.field_id] = '';
    } else if (stype === 'textarea_group') {
      for (const field of section.fields || []) {
        data[field.id] = '';
      }
    } else if (stype === 'yes_no_field') {
      data[section.field_id] = '';
    } else if (stype === 'signatures') {
      for (const field of section.fields || []) {
        data[field.id] = field.type === 'date' ? today : '';
      }
    }
  }

  return data;
}

export default function StructuredCompetencyForm({
  open,
  onOpenChange,
  employeeId,
  employeeName,
  token,
  assessorName,
  onSuccess,
}) {
  const [step, setStep] = useState('choose');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('choose');
    setSelectedTemplate(null);
    setFormData({});
    fetchTemplates();
  }, [open]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/competency-form-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to load competency templates:', error);
      toast.error('Failed to load competency forms');
    } finally {
      setLoading(false);
    }
  };

  const startTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData(buildInitialFormData(template, employeeName, assessorName));
    setStep('form');
  };

  const updateField = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const questionSections = useMemo(
    () =>
      (selectedTemplate?.sections || []).filter((s) =>
        ['yes_no_questions', 'yes_no_na_questions'].includes(s.type),
      ),
    [selectedTemplate],
  );

  const answeredCount = useMemo(() => {
    let total = 0;
    let answered = 0;
    for (const section of questionSections) {
      const allowNa = section.type === 'yes_no_na_questions';
      for (const q of section.questions || []) {
        total += 1;
        const val = formData[q.id];
        if (val === 'yes' || val === 'no' || (allowNa && val === 'na')) answered += 1;
      }
    }
    return { total, answered };
  }, [formData, questionSections]);

  const handleSubmit = async () => {
    if (!selectedTemplate) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/competencies/structured`,
        {
          template_id: selectedTemplate.template_id,
          form_data: formData,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success(`${selectedTemplate.title} saved`);
      onOpenChange(false);
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.errors?.length) {
        toast.error(detail.errors[0]);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error('Failed to save competency form');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderHeaderFields = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border">
      {(selectedTemplate?.header_fields || []).map((field) => (
        <div key={field.id}>
          <Label className="text-xs text-gray-600">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          <Input
            type={field.type === 'date' ? 'date' : 'text'}
            value={formData[field.id] || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
            className="mt-1"
          />
        </div>
      ))}
    </div>
  );

  const renderSection = (section) => {
    const stype = section.type;

    if (stype === 'yes_no_questions' || stype === 'yes_no_na_questions') {
      const allowNa = stype === 'yes_no_na_questions';
      return (
        <div key={section.id} className="space-y-3 p-4 border rounded-lg bg-white">
          <h3 className="font-semibold text-primary text-sm">{section.title}</h3>
          <div className="space-y-3">
            {(section.questions || []).map((q) => (
              <div key={q.id} className="border rounded-lg p-3 bg-white space-y-2">
                <p className="text-sm text-gray-900">{q.label}</p>
                <AnswerButtons
                  value={formData[q.id]}
                  onChange={(v) => updateField(q.id, v)}
                  allowNa={allowNa}
                />
                <Textarea
                  placeholder="Evidence / comments (optional)"
                  value={formData[`${q.id}_comments`] || ''}
                  onChange={(e) => updateField(`${q.id}_comments`, e.target.value)}
                  rows={2}
                  className="text-sm"
                />
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (stype === 'checklist_table') {
      return (
        <div key={section.id} className="space-y-3 p-4 border rounded-lg bg-white">
          <h3 className="font-semibold text-primary text-sm">{section.title}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border p-2 text-left">Activity</th>
                  {(section.columns || []).map((col) => (
                    <th key={col.id} className="border p-2 text-left">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(section.items || []).map((item) => (
                  <tr key={item.id}>
                    <td className="border p-2">{item.label}</td>
                    {(section.columns || []).map((col) => (
                      <td key={col.id} className="border p-1">
                        {col.id === 'competent' ? (
                          <select
                            className="w-full h-8 text-xs border rounded px-1"
                            value={formData[`checklist_${item.id}_${col.id}`] || ''}
                            onChange={(e) => updateField(`checklist_${item.id}_${col.id}`, e.target.value)}
                          >
                            <option value="">—</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        ) : (
                          <Input
                            type="date"
                            value={formData[`checklist_${item.id}_${col.id}`] || ''}
                            onChange={(e) => updateField(`checklist_${item.id}_${col.id}`, e.target.value)}
                            className="h-8 text-xs"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (stype === 'textarea') {
      return (
        <div key={section.id} className="space-y-2 p-4 border rounded-lg bg-white">
          <Label className="font-medium text-sm">
            {section.label || section.title}
            {section.required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          <Textarea
            rows={4}
            value={formData[section.field_id] || ''}
            onChange={(e) => updateField(section.field_id, e.target.value)}
          />
        </div>
      );
    }

    if (stype === 'textarea_group') {
      return (
        <div key={section.id} className="space-y-3 p-4 border rounded-lg bg-white">
          <h3 className="font-semibold text-primary text-sm">{section.title}</h3>
          {(section.fields || []).map((field) => (
            <div key={field.id} className="space-y-2">
              <Label className="text-sm">{field.label}</Label>
              <Textarea
                rows={3}
                value={formData[field.id] || ''}
                onChange={(e) => updateField(field.id, e.target.value)}
              />
            </div>
          ))}
        </div>
      );
    }

    if (stype === 'yes_no_field') {
      return (
        <div key={section.id} className="space-y-2 p-4 border rounded-lg bg-white">
          <Label className="font-medium text-sm">
            {section.label}
            {section.required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          <AnswerButtons
            value={formData[section.field_id]}
            onChange={(v) => updateField(section.field_id, v)}
            allowNa={false}
          />
        </div>
      );
    }

    if (stype === 'signatures') {
      return (
        <div key={section.id} className="space-y-3 p-4 bg-slate-50 rounded-lg border">
          <h3 className="font-semibold text-sm">{section.title}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(section.fields || []).map((field) => (
              <div key={field.id}>
                <Label className="text-xs text-gray-600">{field.label}</Label>
                <Input
                  type={field.type === 'date' ? 'date' : 'text'}
                  value={formData[field.id] || ''}
                  onChange={(e) => updateField(field.id, e.target.value)}
                  className="mt-1"
                />
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] max-h-[90vh] !flex flex-col overflow-hidden p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            {step === 'choose' ? 'Start competency assessment' : selectedTemplate?.title}
          </DialogTitle>
          <DialogDescription>
            {step === 'choose'
              ? 'Choose the approved competency form. Complete all sections, then save to generate a branded PDF.'
              : `Answer all criteria (${answeredCount.answered}/${answeredCount.total}) — scroll down for all sections`}
          </DialogDescription>
        </DialogHeader>

        {step === 'choose' ? (
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {templates.map((tpl) => (
                  <button
                    key={tpl.template_id}
                    type="button"
                    onClick={() => startTemplate(tpl)}
                    className="text-left p-4 rounded-xl border-2 border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors"
                    data-testid={`start-competency-${tpl.template_id}`}
                  >
                    <p className="font-semibold text-gray-900">{tpl.title}</p>
                    <p className="text-sm text-gray-500 mt-1">Approved workspace competency assessment</p>
                    <Badge variant="outline" className="mt-3">
                      {(tpl.sections || []).length} sections
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="shrink-0 px-6 py-2 border-b bg-slate-50 text-xs text-gray-600 flex flex-wrap gap-x-4">
              <span><strong>{answeredCount.answered}</strong> / {answeredCount.total} criteria answered</span>
              <span className="text-primary font-medium">Scroll down ↓</span>
            </div>
            <div
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4 space-y-6"
              data-testid="structured-competency-scroll"
            >
              {renderHeaderFields()}
              {(selectedTemplate?.sections || []).map((section) => renderSection(section))}
              <div className="text-center text-xs text-gray-400 py-4 border-t">
                End of form — review answers, then click Save &amp; generate PDF.
              </div>
            </div>
            <DialogFooter className="px-6 py-4 border-t shrink-0 bg-white">
              <Button variant="outline" onClick={() => setStep('choose')} disabled={submitting}>
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="save-structured-competency">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save &amp; generate PDF
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
